import type { PageTab } from '@mdvip/shared/components/blocks/PageTabs';
import { PageTabs } from '@mdvip/shared/components/blocks/PageTabs';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { PastTransactionsPage } from './PastTransactionsPage';
import { PaymentAccountsPage } from './PaymentAccountsPage';
import { PaymentCenterPage } from './PaymentCenterPage';

/** Deep-link that opens AutoPay enrollment straight from an email/SMS link. */
const AUTOPAY_PARAM = 'turnOnAutopay';

/** Deep-link that opens the one-time payment modal, e.g. from a balance-due reminder email. */
const MAKE_PAYMENT_PARAM = 'makePayment';

/**
 * The active tab lives in the URL so the Recent Transactions widget can link to the full history,
 * and so a member can bookmark or share a tab. The default tab is represented by the param's
 * absence, which keeps a plain `/payments` link clean.
 */
const TAB_PARAM = 'tab';
const TAB_VALUES = ['payment-center', 'payment-accounts', 'past-transactions'] as const;
type PaymentsTab = (typeof TAB_VALUES)[number];
const DEFAULT_TAB: PaymentsTab = 'payment-center';

function isPaymentsTab(value: string | null): value is PaymentsTab {
  return value !== null && TAB_VALUES.includes(value as PaymentsTab);
}

export function PaymentsPage() {
  const { t } = useTranslation('members');
  const [searchParams, setSearchParams] = useSearchParams();

  // The AutoPay card lives on the Payment Center tab, which is already the default, so the
  // deep-link needs no tab switching, it only has to open the enrollment modal.
  const autoOpenEnrollment = searchParams.get(AUTOPAY_PARAM) === 'true';

  // Consume the deep-link once the member dismisses the modal so a refresh (or back/forward)
  // doesn't reopen it. `replace` keeps it out of history; only this key is dropped, so any
  // unrelated params survive.
  const clearAutoPayDeepLink = useCallback(() => {
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete(AUTOPAY_PARAM);
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  // Same shape as the AutoPay deep-link: the one-time payment modal also lives on the default tab.
  const autoOpenPayment = searchParams.get(MAKE_PAYMENT_PARAM) === 'true';

  const clearMakePaymentDeepLink = useCallback(() => {
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete(MAKE_PAYMENT_PARAM);
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  const requestedTab = searchParams.get(TAB_PARAM);
  // An unknown or malformed tab value falls back to the default rather than rendering no panel.
  const activeTab: PaymentsTab = isPaymentsTab(requestedTab) ? requestedTab : DEFAULT_TAB;

  // Pushed, not replaced, so Back returns to the tab the member came from. Unrelated params (the
  // AutoPay deep-link) survive the write.
  const selectTab = useCallback(
    (tab: string) => {
      setSearchParams((current) => {
        const next = new URLSearchParams(current);
        if (tab === DEFAULT_TAB) {
          next.delete(TAB_PARAM);
        } else {
          next.set(TAB_PARAM, tab);
        }
        return next;
      });
    },
    [setSearchParams],
  );

  // Glyphs come straight from the design handoff for this tab bar.
  const tabs: PageTab[] = [
    { value: 'payment-center', label: t('payments.tabs.paymentCenter'), icon: 'creditCard' },
    { value: 'payment-accounts', label: t('payments.tabs.paymentAccounts'), icon: 'landmark' },
    { value: 'past-transactions', label: t('payments.tabs.pastTransactions'), icon: 'clock' },
  ];

  return (
    <>
      <div className="mb-6">
        <h1 className="text-h1 font-semibold">{t('payments.title')}</h1>
        <p className="text-sm text-muted-foreground mt-1">{t('payments.subtitle')}</p>
      </div>
      {/* Controlled mode renders only the active tab's content, so each panel mounts on demand. */}
      <PageTabs tabs={tabs} value={activeTab} onValueChange={selectTab}>
        {activeTab === 'payment-center' && (
          <PaymentCenterPage
            autoOpenEnrollment={autoOpenEnrollment}
            onEnrollmentClose={clearAutoPayDeepLink}
            autoOpenPayment={autoOpenPayment}
            onPaymentClose={clearMakePaymentDeepLink}
            onViewAllTransactions={() => selectTab('past-transactions')}
            onManagePaymentAccounts={() => selectTab('payment-accounts')}
          />
        )}
        {activeTab === 'payment-accounts' && <PaymentAccountsPage />}
        {activeTab === 'past-transactions' && <PastTransactionsPage />}
      </PageTabs>
    </>
  );
}
